import { useContext, useEffect } from "react";
import { IconsTrafficLightStatusCodes } from "constants/constants";
import { ClusterContext } from "contexts/ClusterContext/ClusterContext";
import { IMachineCentersClusterStatus } from "contexts/ClusterContext/ClusterContextInterfaces";
import { IClusterHMIProps } from "pages/HMI/components/ClusterHMICard/ClusterHMICardInterfaces";
import { getMachineCentersClusterStatusLocalStorage } from "utilities/storageUtilities";

/**
 * Keeps `machineCentersClusterStatus` aligned with the machine centers currently shown on
 * the cluster dashboard. That map is otherwise built only at login, so a machine center
 * added to the cluster mid-session has no entry — and the cluster MQTT handler drops state
 * messages for machine centers it can't find in the map, leaving the new card stuck on its
 * default "running" state until a full re-login.
 *
 * Reconciliation is two-way: a default "running" entry is seeded for each newly-shown
 * machine center (so the existing handler can pick up its live state immediately), and
 * entries for machine centers no longer on the dashboard are pruned so they can't linger
 * with stale state and resurface (e.g. as a stale red) if the same machine center is later
 * re-added. Entries that are still on the dashboard are carried over untouched so
 * in-progress downtime state is never disturbed.
 *
 * The current map is read from localStorage — the same source of truth the cluster MQTT
 * handler reads and writes — rather than from the React-state snapshot. The setter writes
 * localStorage synchronously but React state asynchronously, so the snapshot can lag; were
 * we to reconcile against it we could write a stale map back and clobber a downtime update
 * that landed first.
 */
export const useReconcileClusterStatus = (clusterHMIs: IClusterHMIProps["machineCenter"][]): void => {
    const { machineCentersClusterStatus, handleSetMachineCentersClusterStatus } = useContext(ClusterContext);

    useEffect(() => {
        const currentStatus = getMachineCentersClusterStatusLocalStorage() ?? [];
        const knownIds = new Set(currentStatus.map((status) => status.machineCenterId));
        const visibleIds = new Set(clusterHMIs.map((hmi) => hmi.id));

        const missing = clusterHMIs.filter((hmi) => !knownIds.has(hmi.id));
        const retained = currentStatus.filter((status) => visibleIds.has(status.machineCenterId));

        // Nothing to seed and nothing to prune — leave the map untouched.
        if (missing.length === 0 && retained.length === currentStatus.length) {
            return;
        }

        handleSetMachineCentersClusterStatus([
            ...retained,
            ...missing.map(
                (hmi): IMachineCentersClusterStatus => ({
                    machineCenterId: hmi.id,
                    machinePlcDisconnected: false,
                    notSelectedOrBelowTriggerEventReportId: undefined,
                    eventLocalServerUuid: undefined,
                    failureDetails: undefined,
                    status: IconsTrafficLightStatusCodes.UP,
                    downtimePending: false,
                    counter: undefined,
                }),
            ),
        ]);
    }, [clusterHMIs, machineCentersClusterStatus, handleSetMachineCentersClusterStatus]);
};
